import { useProfile } from '@ninetailed/experience.js-next';
import laggy from 'api/middleware/laggy';
import { isPlanFromAPI } from 'api/savings-plans';
import { useAuth } from 'contexts/auth';
import { useReferalToken } from 'contexts/referal';
import { findCartItemByVariantId } from 'hooks/savings-plan/utils';
import { useRegion } from 'hooks/use-region';
import { useEffect, useMemo } from 'react';
import { useSavingPlanStore } from 'stores/saving-plan';
import useSWR from 'swr';
import { fetchEffectiveCart } from '../../api/cart';
import { CountryCode } from '../../config/constants/locales';
import { SubscriptionInterval } from '../../constants/order';
import { Address } from '../../interfaces/address';
import { EffectiveCart } from '../../interfaces/cart';
import { ShippingMethod } from '../../interfaces/shipping';
import * as AddressModel from '../../models/address';

type EffectiveCartDiscountPayload =
  { type: 'Coupon'; code: string } | { type: 'Referal'; code: string } | { type: 'Credit' };

interface EffectiveCartShippingInput {
  address?: any;
  country?: CountryCode; // should be removed
  price?: number;
  priceReduced?: number;
  userChoice?: ShippingMethod;
  title?: string;
}

interface RedemptionCartItem {
  quantity: number;
  variantId: number;
  /** Points for redemption gift  */
  points: number;
  /** True if item should be treated as redemption gift */
  redeemItem: true;
}

export interface CreateEffectiveCartPayloadInput {
  items: Array<
    | RedemptionCartItem
    | {
        price?: number;
        variantId: number;
        quantity: number;
        interval?: SubscriptionInterval;
      }
  >;
  useCredits: boolean;
  shippingMethod: ShippingMethod | null;
  shippingAddress: Address | null;
  couponCode?: string | null;
  recurring?: boolean | null;
  expressCheckoutCustomerId?: number | null;
}

interface useEffectiveCartState {
  effectiveCart: EffectiveCart | undefined;
  loading: boolean;
  refetch: () => Promise<EffectiveCart | undefined>;
  error: any;
}

export interface EffectiveCartPayload {
  items: Array<
    | RedemptionCartItem
    | {
        price?: number;
        variantId: number;
        quantity: number;
        interval?: SubscriptionInterval;
      }
  >;
  gifts?: {
    donated?: boolean;
    selected?: string | null;
  };
  shipping?: EffectiveCartShippingInput | undefined;
  recurring?: boolean | null;
  discount?: EffectiveCartDiscountPayload | undefined;
  customerId?: number | null;
  audiences?: string[];
}

/**
 * Builds a stable "effective cart" payload from current cart inputs and user/session state.
 *
 * Used by {@link useEffectiveCart} to request/refresh the calculated cart (prices, discounts, shipping, etc.).
 * The payload is memoized and includes:
 * - **items**: normalized cart items (one-off / subscription / redemption gifts)
 * - **discount**: referral token → coupon code → credits (in that priority order)
 * - **shipping**: formatted shipping address + selected shipping method (if provided)
 * - **customerId**: taken from auth customer when available, otherwise from express checkout id
 */
export const useEffectiveCartPayload = ({
  items,
  shippingAddress,
  shippingMethod,
  recurring = null,
  couponCode,
  useCredits,
  expressCheckoutCustomerId = null,
}: CreateEffectiveCartPayloadInput): EffectiveCartPayload => {
  const { customer } = useAuth();
  const { referalToken } = useReferalToken();
  const { profile } = useProfile();
  const audiencesKey = profile?.audiences?.join(',');
  const discount: EffectiveCartDiscountPayload | undefined = useMemo(() => {
    if (typeof referalToken === 'string') {
      return { type: 'Referal', code: referalToken };
    }

    if (couponCode) {
      return { type: 'Coupon', code: couponCode };
    }

    if (!customer) {
      return undefined;
    }

    if (useCredits) {
      return { type: 'Credit', customerId: customer.id };
    }

    return undefined;
  }, [referalToken, couponCode, customer, useCredits]);

  const shipping = useMemo(
    () =>
      !shippingAddress && !shippingMethod
        ? undefined
        : {
            address: shippingAddress ? AddressModel.toPdServerFormat(shippingAddress) : undefined,
            type: shippingMethod ?? undefined,
          },
    [shippingMethod, shippingAddress]
  );

  return useMemo(
    () => ({
      items: items.map((v) => ({ ...v, variantId: v.variantId })),
      shipping: shipping as EffectiveCartShippingInput | undefined,
      recurring: recurring as boolean | null,
      discount,
      customerId: customer?.customerId || expressCheckoutCustomerId,
      audiences: audiencesKey ? audiencesKey.split(',') : undefined,
    }),
    [
      customer?.customerId,
      discount,
      shipping,
      expressCheckoutCustomerId,
      items,
      recurring,
      audiencesKey,
    ]
  );
};

/**
 * Hooks for EffectiveCart.
 * Laggy is used to maintain data to show while new response is being fetching
 */
export const useEffectiveCart = (
  payload?: Partial<ReturnType<typeof useEffectiveCartPayload>>
): useEffectiveCartState => {
  const region = useRegion();
  const activePlan = useSavingPlanStore((state) => state.activePlan);
  const isPlanFromCustomer = useSavingPlanStore((state) => state.isPlanFromCustomer);
  const hasHydrated = useSavingPlanStore((state) => state._hasHydrated);

  const ruleId = useMemo(() => {
    if (!activePlan) return undefined;

    if (isPlanFromCustomer) return activePlan.ruleId;

    // For drafted plans, we only want to apply the rule if the plan is actually in the cart
    if (isPlanFromAPI(activePlan)) {
      const isPlanInCart = findCartItemByVariantId(
        payload?.items || [],
        String(activePlan.variantId)
      );

      if (isPlanInCart) return activePlan.ruleId;
    }

    return undefined;
  }, [activePlan, isPlanFromCustomer, payload?.items]);

  // Convert to number because legacy stacks accepting only number
  const _payload = {
    ...payload,
    items: payload?.items?.map((v) => ({
      ...v,
      variantId: Number(v.variantId),
    })),
    ruleId,
  };

  const { data, mutate, error, isValidating } = useSWR<
    EffectiveCart | undefined,
    Error,
    [string | undefined, typeof _payload, boolean] | null
  >(
    hasHydrated ? [region, _payload, hasHydrated] : null,
    ([, _payload]) => fetchEffectiveCart(_payload),
    {
      errorRetryCount: 1,
      use: [laggy],
    }
  );

  useEffect(() => {
    if (error) {
      console.error('effective cart error', error);
    }
  }, [error]);

  return {
    effectiveCart: data,
    loading: isValidating,
    refetch: mutate,
    error,
  };
};
